import React from "react";
import Select, { SingleValue } from "react-select";

interface SelectOption<T> {
  raw: T;
  label: React.ReactNode;
  value: string;
}

interface SelectDropdownProps<T> {
  data: T[];
  value: T | null;
  onChange: (value: T) => void;
  placeholder?: string;

  /** Convert item → unique value */
  getValue: (item: T) => string;

  /** Convert item → label text / JSX */
  getLabel: (item: T) => React.ReactNode;

  /** Custom dropdown row UI */
  formatOptionLabel?: (item: T) => React.ReactNode;

  width?: string | number;
  isSearchable?: boolean;
  currency?: boolean;
  backgroundColor?: string;
  isClearable?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  placeHolderFontSize?: string;
  textSize?: string;
  fontWeight?: number;
  placeHolderColor?: string;
  menuPlacement?: "auto" | "top" | "bottom";
}

function SelectDropdown<T>({
  data,
  value,
  onChange,
  getValue,
  getLabel,
  formatOptionLabel,
  placeholder = "Select",
  width = "100%",
  isSearchable = true,
  currency = false,
  backgroundColor = "#fff",
  isClearable = false,
  isLoading = false,
  isDisabled = false,
  placeHolderFontSize = "16px",
  textSize = "20px",
  fontWeight = 400,
  placeHolderColor = "#000",
  menuPlacement = "auto",
}: SelectDropdownProps<T>) {
  const options: SelectOption<T>[] = data.map((item) => ({
    raw: item,
    label: getLabel(item),
    value: getValue(item),
  }));

  const selected = value ? options.find((o) => getValue(o.raw) === getValue(value)) || null : null;

  const controlStyle = currency
    ? {
        padding: "0 2px",
        borderRadius: "none",
        borderTopRightRadius: "8px",
        borderBottomRightRadius: "8px",
      }
    : {
        borderRadius: "8px",
        padding: "15px 16px",
      };

  const valueContainerStyle = currency ? { paddingLeft: "12%" } : {};

  const styles = {
    control: (base: any) => ({
      ...base,
      backgroundColor,
      alignSelf: "stretch",
      color: "#000",
      fontFamily: "Inter",
      fontSize: textSize,
      fontWeight: fontWeight,
      fontStyle: "normal",
      height: "100%",
      width,
      borderColor: "#e5e7eb",
      boxShadow: "none",
      cursor: "pointer",
      outline: "none",
      ":hover": {
        borderColor: "#e5e7eb",
      },
      ...controlStyle,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    valueContainer: (base: any) => ({ ...base, padding: 0, margin: 0, ...valueContainerStyle }),
    input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
    placeholder: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
      fontSize: placeHolderFontSize,
      color: placeHolderColor,
      fontWeight: 400,
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 9999,
      fontFamily: "Inter",
      fontSize: "14px",
      maxWidth: width,
    }),
    option: (base: any, state: { isFocused: boolean }) => ({
      ...base,
      backgroundColor: state.isFocused ? "#f0f0f0" : "#fff",
      color: "#425466",
      padding: "10px 18px",
      cursor: "pointer",
    }),
    singleValue: (base: any) => ({ ...base, margin: 0, color: "#000" }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: "0px",
      paddingRight: "1px",
    }),
  };

  const handleChange = (option: SingleValue<SelectOption<T>>) => {
    if (option === null) {
      onChange(null as any);
      return;
    }
    onChange(option.raw);
  };

  return (
    <Select
      options={options}
      value={selected}
      onChange={handleChange}
      placeholder={placeholder}
      styles={styles}
      isSearchable={isSearchable}
      isClearable={isClearable}
      isLoading={isLoading}
      isDisabled={isDisabled}
      components={{ IndicatorSeparator: () => null }}
      formatOptionLabel={(option) =>
        formatOptionLabel ? (
          formatOptionLabel(option.raw)
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "start",
              gap: "14px",
              maxWidth: width,
            }}
            id={option.value}
          >
            <div>
              <div style={{ fontSize: "16px", color: "#000" }}>{option.label}</div>
            </div>
          </div>
        )
      }
      menuPlacement={menuPlacement}
    />
  );
}

export default SelectDropdown;
